import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { documentTitle } from "encamp-shared/src/waste/shippingDocumentLabels";

import { REMOVE_EPISODIC_MANIFEST_EXEMPTION_MUTATION } from "./api";
import { formatPounds } from "./episodicDisplay";
import { describeAttachError } from "./episodicErrors";
import { calculatedOf, declarationOf, EventStreamRow } from "./eventManifestRows";
import { MANIFEST_REFETCH_QUERIES } from "./manifestExemptionForm";

export type ConfirmTarget = {
  exemptionId: string;
  header: string;
  info: string;
  confirmText: string;
  successText: string;
};

export function removeConfirmFor(row: EventStreamRow): ConfirmTarget | null {
  const declaration = declarationOf(row.basis);
  if (!declaration) {
    return null;
  }
  const label = `${row.wasteStream.name} on ${documentTitle(row)}`;
  const calculated = calculatedOf(row.basis);
  if (calculated) {
    return {
      exemptionId: declaration.id,
      header: `Remove the entered pounds for ${label}?`,
      info: `The ${formatPounds(calculated.pounds)} from containers counts instead.`,
      confirmText: "Remove",
      successText: "Back to the container total",
    };
  }
  return {
    exemptionId: declaration.id,
    header: `Remove the declaration for ${label}?`,
    info: "Its pounds count toward generator status again.",
    confirmText: "Remove",
    successText: "Declaration removed",
  };
}

export function useRemoveExemption(extraRefetchQueries: string[] = []) {
  const alerts = useAlerts();
  const [removeExemption, { loading: removing }] = useMutation(
    REMOVE_EPISODIC_MANIFEST_EXEMPTION_MUTATION,
    { refetchQueries: [...MANIFEST_REFETCH_QUERIES, ...extraRefetchQueries] }
  );
  return {
    removing,
    remove: (target: ConfirmTarget, onSettled: (removed: boolean) => void) =>
      removeExemption({
        variables: { exemptionId: target.exemptionId },
        onCompleted: () => {
          alerts.success(target.successText);
          onSettled(true);
        },
        onError: (error) => {
          alerts.error(describeAttachError(error, "The declaration could not be removed"));
          onSettled(false);
        },
      }),
  };
}
